// ScheduleLegend — compact, dismissible key decoding the schedule's
// colour system. Two groups: (a) the six activity-type hairline
// colours (≤8px dot + kind label); (b) the severity treatments (red
// overdue / brand due-soon / amber slippage sample chips). Follows the
// OccurrenceStripKey pattern (dismissible, localStorage-persisted,
// show/hide affordance) but DEFAULTS VISIBLE — brand rule R5: six type
// colours plus the severity set earn out-of-the-box discoverability
// (mirrors OccurrenceStripKey's many-state default-visible reasoning).
// Persistence key: "schedule.legend.dismissed".
//
// Swatches are rendered via the shared LegendSwatch primitive so the
// type-dot vs severity-block vs today-ring vocabulary is single-sourced
// with the density heatmap's footer legend.

import { useCallback, useState } from "react";
import { HelpCircle, X } from "lucide-react";
import LegendSwatch, { type SwatchTone } from "./legend/LegendSwatch";
import {
  KIND_LABEL,
  SCHEDULE_KINDS,
  type ScheduleKind,
} from "./kindVocabulary";

const STORAGE_KEY = "schedule.legend.dismissed";

function readDismissed(): boolean {
  // Default VISIBLE — only an explicit stored "1" dismisses. Optional
  // chaining + try/catch covers SSR and storage-disabled contexts.
  try {
    return window?.localStorage?.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(dismissed: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, dismissed ? "1" : "0");
  } catch {
    // best-effort
  }
}

// ScheduleLegend's type swatches project the KIND_DOT_BG bg-class tone
// of each kind into the LegendSwatch's tone-keyword vocabulary. Kept
// local here because LegendSwatch is keyword-driven and the kind
// vocabulary is class-driven — each surface picks the projection it
// wants without forcing the kind vocabulary itself to know LegendSwatch.
const KIND_SWATCH_TONE: Record<ScheduleKind, SwatchTone> = {
  insurance: "amber",
  deliverable: "sky",
  milestone: "fuchsia",
  meeting: "brand",
  kpi: "teal",
};

const SEVERITY_SWATCHES: { key: string; tone: SwatchTone; label: string }[] = [
  { key: "red", tone: "red", label: "Overdue" },
  { key: "brand", tone: "brand", label: "Due soon" },
  { key: "amber", tone: "amber", label: "Slippage" },
];

export default function ScheduleLegend() {
  // readDismissed passed as lazy initialiser reads localStorage exactly once on
  // mount — no post-mount effect needed. This is a client-only SPA so there is
  // no SSR hydration mismatch to guard against.
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    writeDismissed(true);
  }, []);

  const handleShow = useCallback(() => {
    setDismissed(false);
    writeDismissed(false);
  }, []);

  if (dismissed) {
    return (
      <button
        type="button"
        onClick={handleShow}
        aria-label="Show schedule legend"
        data-testid="schedule-legend-show"
        className="inline-flex items-center gap-1 rounded-md p-1 text-caption text-slate-500 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Schedule legend</span>
      </button>
    );
  }

  return (
    <div
      data-testid="schedule-legend"
      role="note"
      aria-label="Schedule legend"
      className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60"
    >
      <span className="text-caption font-medium text-slate-700 dark:text-slate-200">
        Type:
      </span>
      {SCHEDULE_KINDS.map((kind) => (
        <LegendSwatch
          key={kind}
          testId={`schedule-legend-type-${kind}`}
          label={KIND_LABEL[kind]}
          shape="dot"
          tone={KIND_SWATCH_TONE[kind]}
        />
      ))}
      <span className="ml-2 text-caption font-medium text-slate-700 dark:text-slate-200">
        Status:
      </span>
      {SEVERITY_SWATCHES.map(({ key, tone, label }) => (
        <LegendSwatch
          key={key}
          testId={`schedule-legend-severity-${key}`}
          label={label}
          shape="block"
          tone={tone}
        />
      ))}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss schedule legend"
        data-testid="schedule-legend-dismiss"
        className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
